import React, { useEffect, useRef, useState } from "react";
import { Input } from "reactstrap";

const isValidationResultValid = (validation) =>
  validation === true ||
  (validation && typeof validation === "object" && validation.valid === true);

/**
 * Always-visible text input.
 * - Default: submit on Enter or blur
 * - With `validate`: invalid values get a red border (typed value kept)
 * - `submitOnBlur={false}`: blur only validates; API save only on Enter / Tab
 * - `skipUnchangedCheck`: always call onSave even if draft matches `value`
 */
const InlineEditableField = ({
  value = "",
  onSave,
  validate,
  submitOnBlur = true,
  skipUnchangedCheck = false,
  disabled = false,
  saving = false,
  inputProps = {},
  className = "",
}) => {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [invalid, setInvalid] = useState(false);
  const isSubmittingRef = useRef(false);
  const draftRef = useRef(draft);
  const invalidRef = useRef(false);
  const suppressBlurSaveRef = useRef(false);

  useEffect(() => {
    if (invalidRef.current) return;
    setDraft(String(value ?? ""));
  }, [value]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    invalidRef.current = invalid;
  }, [invalid]);

  const runValidate = (next) => {
    if (typeof validate !== "function") return true;
    return isValidationResultValid(validate(next));
  };

  const save = async () => {
    if (disabled || saving || isSubmittingRef.current || typeof onSave !== "function") {
      return;
    }
    const next = String(draftRef.current ?? "");

    if (!runValidate(next)) {
      setInvalid(true);
      return;
    }

    setInvalid(false);

    if (
      !skipUnchangedCheck &&
      String(next).trim() === String(value ?? "").trim()
    ) {
      return;
    }

    isSubmittingRef.current = true;
    try {
      const result = await onSave(next);
      if (result === false) {
        setDraft(String(value ?? ""));
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleBlur = () => {
    const next = String(draftRef.current ?? "");
    if (!runValidate(next)) {
      setInvalid(true);
      suppressBlurSaveRef.current = false;
      return;
    }
    setInvalid(false);
    if (suppressBlurSaveRef.current) {
      suppressBlurSaveRef.current = false;
      return;
    }
    if (submitOnBlur) {
      void save();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Enter already saves; skip the follow-up blur save.
      suppressBlurSaveRef.current = true;
      void save().then(() => {
        event.target?.blur?.();
      });
    }
  };

  return (
    <span
      className={`edit-input cd-edit-inline inline-editable-field ${className}`.trim()}
    >
      <Input
        bsSize="sm"
        value={draft}
        disabled={disabled || saving}
        onChange={(event) => {
          setDraft(event.target.value);
          if (invalid) setInvalid(false);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...inputProps}
        invalid={invalid}
        className={[
          "inline-editable-field__input",
          invalid ? "is-invalid" : "",
          inputProps?.className,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    </span>
  );
};

export default InlineEditableField;
